import React from 'react'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Link,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'

import { INTERNAL_CUSTOMERS_ROUTE } from '@core/constants/routes'
import { formatTimeAgo } from '@core/utils/date'

import { Customer } from '../main-types'
import { useListCustomers, useSwitchCustomer } from './admin-api'
import { AdminSettings } from './admin-settings-state'

type AdminSwitchCustomerContentProps = {
  adminSettings: AdminSettings
}

const AdminSwitchCustomerContent = (props: AdminSwitchCustomerContentProps) => {
  const { customer, handleClose } = props.adminSettings
  const [selectedCustomerId, setSelectedCustomerId] = React.useState(customer?.id || '')
  const enableSwitch = selectedCustomerId && selectedCustomerId !== customer!.id
  const { data: customers } = useListCustomers()
  const switchCustomer = useSwitchCustomer()

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault()
        switchCustomer.mutate(selectedCustomerId)
      }}
    >
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 4 }}>
          This feature is moving to the{' '}
          <Link href={INTERNAL_CUSTOMERS_ROUTE} underline="hover">
            Customers list page
          </Link>
          , which offers search, sorting, and category filtering. This dialog will be deprecated
          soon.
        </Alert>
        <Autocomplete
          options={customers}
          sx={{ pb: 3 }}
          onChange={(e, value) => setSelectedCustomerId(value?.id || '')}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label={customers ? 'Select a customer' : 'Loading...'}
              fullWidth
              helperText="All tabs will reload after this change"
              disabled={!customers}
            />
          )}
          value={customers?.find((item) => item.id === selectedCustomerId) || null}
          getOptionLabel={(option) => option.name}
          renderOption={(renderProps, option: Customer) => (
            <ListItem {...renderProps} key={option.id} sx={{ padding: '0px !important' }}>
              <ListItemButton role={undefined} dense>
                <ListItemText
                  id={option.id}
                  color="text.primary"
                  secondaryTypographyProps={{ color: 'primary.main' }}
                  primary={
                    <>
                      {option.id === customer!.id ? '* Current - ' : ''}
                      {option.name}
                    </>
                  }
                  secondary={
                    <>
                      <b>Created at:</b> {formatTimeAgo(option.createdAt)[0]}
                    </>
                  }
                />
              </ListItemButton>
            </ListItem>
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          disabled={!enableSwitch}
          type="submit"
          loading={switchCustomer.isPending}
        >
          Switch Customer
        </LoadingButton>
      </DialogActions>
    </Box>
  )
}

// @TODO: add error boundary
export const AdminSwitchCustomer = (props: AdminSwitchCustomerContentProps) => {
  return (
    <React.Suspense
      fallback={
        <DialogContent>
          <Skeleton variant="rectangular" width="100%" height={118} />
        </DialogContent>
      }
    >
      <AdminSwitchCustomerContent {...props} />
    </React.Suspense>
  )
}
